import Calendar from '-/Custom/Calendar';
import { DateTime } from 'luxon';
import type { CalendarProps } from 'react-native-calendars';
import { withUnistyles } from 'react-native-unistyles';
import { type FragmentOf, graphql, readFragment } from '@/gql/graphql';
import getCalendarMarks from '@/utils/getCalendarMarks';

export const EventCalendarFragment = graphql(/* GraphQL */ `
  fragment EventCalendar on event {
    id
    datetime
  }
`);

type EventCalendarProps = CalendarProps & {
  selected: string[];
  fragment: FragmentOf<typeof EventCalendarFragment>[] | null | undefined;
  disableUnmarkedDays?: boolean;
};

type EventCalendarMarkColors = {
  startingDay: string;
  endingDay: string;
  rangeDay: string;
};

type EventCalendarBaseProps = EventCalendarProps & {
  markColors: EventCalendarMarkColors;
};

function EventCalendarBase({
  selected,
  fragment,
  markColors,
  disableUnmarkedDays = false,
  onDayPress,
  ...calendarProps
}: EventCalendarBaseProps) {
  const events = readFragment(EventCalendarFragment, fragment || []);

  const marked =
    events?.map((event) =>
      DateTime.fromISO(event.datetime || '').toFormat('yyyy-MM-dd'),
    ) || [];

  const markedSet = new Set(marked);
  const resolvedSelected = disableUnmarkedDays
    ? selected.filter((date) => markedSet.has(date))
    : selected;
  const markedDates = getCalendarMarks(marked, resolvedSelected, markColors);

  const resolvedMarkedDates = disableUnmarkedDays
    ? Object.fromEntries(
        Object.entries(markedDates).map(([date, value]) => {
          const hasPip = markedSet.has(date);

          return [
            date,
            {
              ...value,
              disabled: !hasPip,
              disableTouchEvent: !hasPip,
            },
          ];
        }),
      )
    : markedDates;

  return (
    <Calendar
      markedDates={resolvedMarkedDates}
      disabledByDefault={disableUnmarkedDays}
      disableAllTouchEventsForDisabledDays={disableUnmarkedDays}
      onDayPress={(date) => {
        if (disableUnmarkedDays && !markedSet.has(date.dateString)) {
          return;
        }

        onDayPress?.(date);
      }}
      {...calendarProps}
    />
  );
}

const EventCalendar = withUnistyles(EventCalendarBase, (theme) => ({
  markColors: {
    startingDay: theme.colors.primaryActive,
    endingDay: theme.colors.primaryActive,
    rangeDay: theme.colors.primary,
  },
}));

export default EventCalendar;
